import Button from "@/atomic/atoms/Button";
import Header from "@/atomic/atoms/Header";
import { InputMask } from "@react-input/mask";
import { PropsView } from "../Models/address";
import Modal from "@/atomic/atoms/Modal";
import Loading from "@/atomic/atoms/Loading";
import { Suspense } from "react";

const inputBase =
  "w-full h-14 px-4 rounded-xl border font-primary text-base text-text bg-white placeholder:text-tertiary " +
  "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";

function fieldClass(hasError?: boolean) {
  return [inputBase, hasError ? "border-error" : "border-border"].join(" ");
}

export default function RegisterAddressView({
  goBack,
  isDisabled,
  editable,
  isVisibleModal,
  toggleModal,
  isLoading,
  register,
  errors,
  handleSubmit,
}: PropsView) {
  return (
    <div className="h-full relative flex flex-col bg-white overflow-hidden">
      <Header title="Dados pessoais" hasLeftButton goBack={goBack} />
      <main className="flex-1 w-full flex justify-center min-h-0 bg-background">
        <form
          className="w-full max-w-[480px] px-6 pt-6 pb-8 flex flex-col flex-1 min-h-0"
          onSubmit={handleSubmit}
        >
          <div className="flex-1 min-h-0 overflow-y-auto pb-4">
            <h2 className="text-text text-[22px] leading-tight font-primary font-semibold tracking-tight">
              Adicione seu endereço
            </h2>

            <div className="mt-6 flex flex-col gap-5">
              <div>
                <InputMask
                  {...register("zipcode")}
                  id="register-address-zipcode"
                  name="zipcode"
                  required
                  type="text"
                  inputMode="numeric"
                  mask="_____-___"
                  replacement={{ _: /\d/ }}
                  placeholder="Seu CEP"
                  className={fieldClass(!!errors.zipcode)}
                />
                {errors.zipcode && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.zipcode.message as string}
                  </span>
                )}
              </div>

              <div className="flex flex-row gap-3 min-w-0">
                <div className="min-w-0 flex-[7]">
                  <input
                    {...register("streetName")}
                    id="register-address-street"
                    required
                    type="text"
                    name="streetName"
                    placeholder="Nome da rua"
                    disabled={!editable}
                    className={fieldClass(!!errors.streetName)}
                  />
                  {errors.streetName && (
                    <span className="mt-1 block text-error text-xs font-primary">
                      {errors.streetName.message as string}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-[3] max-w-[120px]">
                  <input
                    {...register("number")}
                    id="register-address-number"
                    required
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="number"
                    placeholder="Nº"
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(
                        /\D/g,
                        "",
                      );
                    }}
                    className={fieldClass(!!errors.number)}
                  />
                  {errors.number && (
                    <span className="mt-1 block text-error text-xs font-primary">
                      {errors.number.message as string}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <input
                  {...register("complement")}
                  id="register-address-complement"
                  type="text"
                  name="complement"
                  placeholder="Complemento"
                  className={fieldClass(!!errors.complement)}
                />
                {errors.complement && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.complement.message as string}
                  </span>
                )}
              </div>

              <div>
                <input
                  {...register("neighborhood")}
                  id="register-address-neighborhood"
                  required
                  type="text"
                  name="neighborhood"
                  placeholder="Bairro"
                  disabled={!editable}
                  className={fieldClass(!!errors.neighborhood)}
                />
                {errors.neighborhood && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.neighborhood.message as string}
                  </span>
                )}
              </div>

              <div className="flex flex-row gap-3 min-w-0">
                <div className="min-w-0 flex-[13]">
                  <input
                    {...register("city")}
                    id="register-address-city"
                    required
                    type="text"
                    name="city"
                    placeholder="Cidade"
                    disabled={!editable}
                    className={fieldClass(!!errors.city)}
                  />
                  {errors.city && (
                    <span className="mt-1 block text-error text-xs font-primary">
                      {errors.city.message as string}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-[7] max-w-[100px]">
                  <InputMask
                    {...register("state")}
                    id="register-address-state"
                    name="state"
                    required
                    type="text"
                    mask="__"
                    replacement={{ _: /\D/ }}
                    placeholder="UF"
                    disabled={!editable}
                    className={`${fieldClass(!!errors.state)} uppercase`}
                  />
                  {errors.state && (
                    <span className="mt-1 block text-error text-xs font-primary">
                      {errors.state.message as string}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 shrink-0">
            <Button
              className="rounded-theme-large bg-primary text-white font-primary font-semibold border-0"
              title="Continuar"
              type="submit"
              isDisabled={isDisabled}
            />
          </div>
        </form>
      </main>
      {isVisibleModal && (
        <Suspense fallback={null}>
          <Modal
            title="Atenção"
            message="Cep inválido"
            actionMessage="Usar outro cep"
            action={toggleModal}
          />
        </Suspense>
      )}
      {isLoading && (
        <Suspense fallback={null}>
          <Loading />
        </Suspense>
      )}
    </div>
  );
}
